import {
  DISPATCH_ACCESS_TO_SPECIFIC_TRAINING_OPTIONS,
  DISPATCH_BASIC_TELECOMMUNICATOR_TRAINING_OPTIONS,
  DISPATCH_CAPABILITIES_PART_COUNT,
  DISPATCH_CAD_SYSTEM_OPTIONS,
  DISPATCH_COMPLETED_TRAINING_CLASS_OPTIONS,
  DISPATCH_CONTINUING_EDUCATION_REQUIRED_OPTIONS,
  DISPATCH_STATE_REQUIRE_CERTIFICATION_OPTIONS,
  DISPATCH_DISCIPLINES_SERVED_OPTIONS,
  DISPATCH_FACILITY_CLASSIFICATION_OPTIONS,
  DISPATCH_MAIN_AREA_OF_RESPONSIBILITY_OPTIONS,
  DISPATCH_PROGRAMS_USED_OPTIONS,
  DISPATCH_PUBLIC_NOTIFICATION_SYSTEM_OPTIONS,
  DISPATCH_SOP_SOG_FOR_TOPICS_OPTIONS,
  type DispatchCapabilitiesPart,
  type RegistrationExtendedFormValues,
} from "@buxus/shared";
import { RegReqLabel } from "../../ui/RegReqLabel";
import { YesNoRadios } from "./YesNoRadios";

type DispatchMultiSelectFieldKey =
  | "dispatchDisciplinesServedByAgency"
  | "dispatchProgramsUsedByAgency"
  | "dispatchTrainingCoversEmergencyTopics"
  | "dispatchHasAccessToEmergencyTopics"
  | "dispatchSopSogForEmergencyTopics"
  | "dispatchCompletedCoursesOnEmergencyTopics";

type DispatchFieldKey =
  | "dispatchFacilityClassification"
  | "dispatchDisciplinesServedByAgency"
  | "dispatchMainAreaOfResponsibility"
  | "dispatchEstPopulationOfResponsibilityArea"
  | "dispatchComputerAidedDispatchSystemUsed"
  | "dispatchComputerAidedDispatchSystemUsedOther"
  | "dispatchCanDetermineCallerLatLongGis"
  | "dispatchProgramsUsedByAgency"
  | "dispatchPublicNotificationSystemUsed"
  | "dispatchPublicNotificationSystemUsedOther"
  | "dispatchIsContinuingEducationRequired"
  | "dispatchDoesStateRequireCertification"
  | "dispatchDoesStateRequireCertificationOther"
  | "dispatchTrainingCoversEmergencyTopics"
  | "dispatchHasAccessToEmergencyTopics"
  | "dispatchSopSogForEmergencyTopics"
  | "dispatchCompletedCoursesOnEmergencyTopics";

type Props = {
  part: DispatchCapabilitiesPart;
  extendedForm: RegistrationExtendedFormValues;
  showFieldError: (field: DispatchFieldKey) => string | undefined;
  onPatch: (partial: Partial<RegistrationExtendedFormValues>) => void;
  onBlur: (field: DispatchFieldKey) => void;
};

function MultiSelectCheckboxes({
  label,
  field,
  options,
  extendedForm,
  onPatch,
  onBlur,
}: {
  label: string;
  field: DispatchMultiSelectFieldKey;
  options: readonly string[];
  extendedForm: RegistrationExtendedFormValues;
  onPatch: (partial: Partial<RegistrationExtendedFormValues>) => void;
  onBlur: (field: DispatchFieldKey) => void;
}) {
  const selected = extendedForm[field];
  return (
    <div className="reg-field reg-field--full">
      <span>{label}</span>
      <div className="reg-checkbox-stack">
        {options.map((option) => (
          <label key={option} className="reg-checkbox-label">
            <input
              type="checkbox"
              checked={selected.includes(option)}
              onChange={() => {
                const next = selected.includes(option)
                  ? selected.filter((x) => x !== option)
                  : [...selected, option];
                onPatch({ [field]: next });
              }}
              onBlur={() => onBlur(field)}
            />
            <span>{option}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

export function DispatchCapabilitiesStep({ part, extendedForm, showFieldError, onPatch, onBlur }: Props) {
  const cadIsOther = extendedForm.dispatchComputerAidedDispatchSystemUsed === "Other";
  const notificationIsOther = extendedForm.dispatchPublicNotificationSystemUsed === "Other";

  const toggleMulti = (field: "dispatchDisciplinesServedByAgency" | "dispatchProgramsUsedByAgency", value: string) => {
    const current = extendedForm[field];
    const next = current.includes(value) ? current.filter((x) => x !== value) : [...current, value];
    onPatch({ [field]: next });
  };

  return (
    <>
      <h2 className="reg-step-title">
        Dispatch Capabilities ({part} of {DISPATCH_CAPABILITIES_PART_COUNT})
      </h2>
      <div className="reg-grid">
        {part === 1 ? (
          <>
            <label className="reg-field">
              <RegReqLabel required>Is your ECC/PSAP classified as:</RegReqLabel>
              <select
                value={extendedForm.dispatchFacilityClassification}
                onChange={(e) => onPatch({ dispatchFacilityClassification: e.target.value })}
                onBlur={() => onBlur("dispatchFacilityClassification")}
              >
                <option value="">Select…</option>
                {DISPATCH_FACILITY_CLASSIFICATION_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
              {showFieldError("dispatchFacilityClassification") ? (
                <small className="reg-field-error">{showFieldError("dispatchFacilityClassification")}</small>
              ) : null}
            </label>

            <div className="reg-field reg-field--full">
              <RegReqLabel required>
                Please select all of the disciplines your agency provides service for:
              </RegReqLabel>
              <div className="reg-checkbox-stack">
                {DISPATCH_DISCIPLINES_SERVED_OPTIONS.map((option) => (
                  <label key={option} className="reg-checkbox-label">
                    <input
                      type="checkbox"
                      checked={extendedForm.dispatchDisciplinesServedByAgency.includes(option)}
                      onChange={() => toggleMulti("dispatchDisciplinesServedByAgency", option)}
                      onBlur={() => onBlur("dispatchDisciplinesServedByAgency")}
                    />
                    <span>{option}</span>
                  </label>
                ))}
              </div>
              {showFieldError("dispatchDisciplinesServedByAgency") ? (
                <small className="reg-field-error">{showFieldError("dispatchDisciplinesServedByAgency")}</small>
              ) : null}
            </div>
          </>
        ) : null}

        {part === 2 ? (
          <>
            <label className="reg-field">
              <RegReqLabel required>
                Which of these most closely represents the majority of your area of responsibility?
              </RegReqLabel>
              <select
                value={extendedForm.dispatchMainAreaOfResponsibility}
                onChange={(e) => onPatch({ dispatchMainAreaOfResponsibility: e.target.value })}
                onBlur={() => onBlur("dispatchMainAreaOfResponsibility")}
              >
                <option value="">Select…</option>
                {DISPATCH_MAIN_AREA_OF_RESPONSIBILITY_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
              {showFieldError("dispatchMainAreaOfResponsibility") ? (
                <small className="reg-field-error">{showFieldError("dispatchMainAreaOfResponsibility")}</small>
              ) : null}
            </label>

            <label className="reg-field">
              <span>Please enter the estimated population of your area of responsibility</span>
              <input
                type="number"
                min={0}
                step={1}
                value={extendedForm.dispatchEstPopulationOfResponsibilityArea}
                onChange={(e) =>
                  onPatch({ dispatchEstPopulationOfResponsibilityArea: e.target.value })
                }
                onBlur={() => onBlur("dispatchEstPopulationOfResponsibilityArea")}
              />
              {showFieldError("dispatchEstPopulationOfResponsibilityArea") ? (
                <small className="reg-field-error">
                  {showFieldError("dispatchEstPopulationOfResponsibilityArea")}
                </small>
              ) : null}
            </label>

            <label className="reg-field">
              <RegReqLabel required>
                What Computer Aided Dispatch (CAD) System does your agency use?
              </RegReqLabel>
              <select
                value={extendedForm.dispatchComputerAidedDispatchSystemUsed}
                onChange={(e) => {
                  const next = e.target.value;
                  onPatch({
                    dispatchComputerAidedDispatchSystemUsed: next,
                    ...(next !== "Other" ? { dispatchComputerAidedDispatchSystemUsedOther: "" } : {}),
                  });
                }}
                onBlur={() => onBlur("dispatchComputerAidedDispatchSystemUsed")}
              >
                <option value="">Select…</option>
                {DISPATCH_CAD_SYSTEM_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
              {showFieldError("dispatchComputerAidedDispatchSystemUsed") ? (
                <small className="reg-field-error">
                  {showFieldError("dispatchComputerAidedDispatchSystemUsed")}
                </small>
              ) : null}
            </label>

            {cadIsOther ? (
              <label className="reg-field">
                <RegReqLabel required>Please specify the CAD system</RegReqLabel>
                <input
                  value={extendedForm.dispatchComputerAidedDispatchSystemUsedOther}
                  onChange={(e) =>
                    onPatch({ dispatchComputerAidedDispatchSystemUsedOther: e.target.value })
                  }
                  onBlur={() => onBlur("dispatchComputerAidedDispatchSystemUsedOther")}
                />
                {showFieldError("dispatchComputerAidedDispatchSystemUsedOther") ? (
                  <small className="reg-field-error">
                    {showFieldError("dispatchComputerAidedDispatchSystemUsedOther")}
                  </small>
                ) : null}
              </label>
            ) : null}

            <YesNoRadios
              name="dispatchCanDetermineCallerLatLongGis"
              required
              label="Do you have the ability to determine the location (lat/long GIS position) of a caller?"
              value={extendedForm.dispatchCanDetermineCallerLatLongGis}
              error={showFieldError("dispatchCanDetermineCallerLatLongGis")}
              onChange={(v) => onPatch({ dispatchCanDetermineCallerLatLongGis: v })}
              onBlur={() => onBlur("dispatchCanDetermineCallerLatLongGis")}
            />

            <label className="reg-field">
              <RegReqLabel required>What public notification system do you use?</RegReqLabel>
              <select
                value={extendedForm.dispatchPublicNotificationSystemUsed}
                onChange={(e) => {
                  const next = e.target.value;
                  onPatch({
                    dispatchPublicNotificationSystemUsed: next,
                    ...(next !== "Other" ? { dispatchPublicNotificationSystemUsedOther: "" } : {}),
                  });
                }}
                onBlur={() => onBlur("dispatchPublicNotificationSystemUsed")}
              >
                <option value="">Select…</option>
                {DISPATCH_PUBLIC_NOTIFICATION_SYSTEM_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
              {showFieldError("dispatchPublicNotificationSystemUsed") ? (
                <small className="reg-field-error">
                  {showFieldError("dispatchPublicNotificationSystemUsed")}
                </small>
              ) : null}
            </label>

            {notificationIsOther ? (
              <label className="reg-field">
                <RegReqLabel required>Please specify the public notification system</RegReqLabel>
                <input
                  value={extendedForm.dispatchPublicNotificationSystemUsedOther}
                  onChange={(e) =>
                    onPatch({ dispatchPublicNotificationSystemUsedOther: e.target.value })
                  }
                  onBlur={() => onBlur("dispatchPublicNotificationSystemUsedOther")}
                />
                {showFieldError("dispatchPublicNotificationSystemUsedOther") ? (
                  <small className="reg-field-error">
                    {showFieldError("dispatchPublicNotificationSystemUsedOther")}
                  </small>
                ) : null}
              </label>
            ) : null}

            <div className="reg-field reg-field--full">
              <span>Please select the program(s) your agency uses:</span>
              <div className="reg-checkbox-stack">
                {DISPATCH_PROGRAMS_USED_OPTIONS.map((option) => (
                  <label key={option} className="reg-checkbox-label">
                    <input
                      type="checkbox"
                      checked={extendedForm.dispatchProgramsUsedByAgency.includes(option)}
                      onChange={() => toggleMulti("dispatchProgramsUsedByAgency", option)}
                      onBlur={() => onBlur("dispatchProgramsUsedByAgency")}
                    />
                    <span>{option}</span>
                  </label>
                ))}
              </div>
            </div>
          </>
        ) : null}

        {part === 3 ? (
          <>
            <label className="reg-field reg-field--full">
              <RegReqLabel required>
                Does your state require telecommunicators to obtain and maintain certification?
              </RegReqLabel>
              <select
                value={extendedForm.dispatchDoesStateRequireCertification}
                onChange={(e) => {
                  const next = e.target.value;
                  onPatch({
                    dispatchDoesStateRequireCertification: next,
                    ...(next !== "Other"
                      ? { dispatchDoesStateRequireCertificationOther: "" }
                      : {}),
                  });
                }}
                onBlur={() => onBlur("dispatchDoesStateRequireCertification")}
              >
                <option value="">Select…</option>
                {DISPATCH_STATE_REQUIRE_CERTIFICATION_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
              {showFieldError("dispatchDoesStateRequireCertification") ? (
                <small className="reg-field-error">
                  {showFieldError("dispatchDoesStateRequireCertification")}
                </small>
              ) : null}
            </label>

            {extendedForm.dispatchDoesStateRequireCertification === "Other" ? (
              <label className="reg-field">
                <RegReqLabel required>Please specify the certification requirement</RegReqLabel>
                <input
                  value={extendedForm.dispatchDoesStateRequireCertificationOther}
                  onChange={(e) =>
                    onPatch({ dispatchDoesStateRequireCertificationOther: e.target.value })
                  }
                  onBlur={() => onBlur("dispatchDoesStateRequireCertificationOther")}
                />
                {showFieldError("dispatchDoesStateRequireCertificationOther") ? (
                  <small className="reg-field-error">
                    {showFieldError("dispatchDoesStateRequireCertificationOther")}
                  </small>
                ) : null}
              </label>
            ) : null}

            <label className="reg-field reg-field--full">
              <RegReqLabel required>Is continuing education required?</RegReqLabel>
              <select
                value={extendedForm.dispatchIsContinuingEducationRequired}
                onChange={(e) =>
                  onPatch({ dispatchIsContinuingEducationRequired: e.target.value })
                }
                onBlur={() => onBlur("dispatchIsContinuingEducationRequired")}
              >
                <option value="">Select…</option>
                {DISPATCH_CONTINUING_EDUCATION_REQUIRED_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
              {showFieldError("dispatchIsContinuingEducationRequired") ? (
                <small className="reg-field-error">
                  {showFieldError("dispatchIsContinuingEducationRequired")}
                </small>
              ) : null}
            </label>

            <MultiSelectCheckboxes
              label="Does your basic telecommunicator training include the following? (Select all that apply)"
              field="dispatchTrainingCoversEmergencyTopics"
              options={DISPATCH_BASIC_TELECOMMUNICATOR_TRAINING_OPTIONS}
              extendedForm={extendedForm}
              onPatch={onPatch}
              onBlur={onBlur}
            />

            <MultiSelectCheckboxes
              label="Do you have access to the following specific training? (Select all that apply)"
              field="dispatchHasAccessToEmergencyTopics"
              options={DISPATCH_ACCESS_TO_SPECIFIC_TRAINING_OPTIONS}
              extendedForm={extendedForm}
              onPatch={onPatch}
              onBlur={onBlur}
            />

            <MultiSelectCheckboxes
              label="Does your agency have a specific SOP/SOG for the following? (Select all that apply)"
              field="dispatchSopSogForEmergencyTopics"
              options={DISPATCH_SOP_SOG_FOR_TOPICS_OPTIONS}
              extendedForm={extendedForm}
              onPatch={onPatch}
              onBlur={onBlur}
            />

            <MultiSelectCheckboxes
              label="Have you completed a training class on any of the following? (Select all that apply)"
              field="dispatchCompletedCoursesOnEmergencyTopics"
              options={DISPATCH_COMPLETED_TRAINING_CLASS_OPTIONS}
              extendedForm={extendedForm}
              onPatch={onPatch}
              onBlur={onBlur}
            />
          </>
        ) : null}
      </div>
    </>
  );
}
